import { useState, type ReactNode } from "react";
import { CheckCircle2, Circle, Copy, CopyCheck, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { describeBrandColor } from "@/features/onboarding/brandColors";
import { cn } from "@/lib/utils";
import type { Client, SubmoduleStatus } from "@/lib/types/client";

/**
 * Die Datenblöcke des Kundenreports, geteilt zwischen der Admin-Ansicht
 * (ClientDetailsPanel) und dem Abschluss-Schritt des Wizards. Beide Seiten
 * sehen damit garantiert dieselben Angaben; die Download-Aktionen sind
 * optional, weil sie nur im Admin existieren.
 */

/** "optional" markiert Blöcke ohne Soll – sie lesen sonst wie offene Aufgaben. */
export type BlockStatus = SubmoduleStatus | "optional";

/**
 * Pro Block-Id ein Häkchen und ein Sprung in den zuständigen Wizard-Schritt.
 * Der Admin-Report übergibt nichts und bleibt dadurch unverändert.
 */
export type BlockMetaMap = Record<string, { status?: BlockStatus; onEdit?: () => void } | undefined>;

function BlockStatusIcon({ status }: { status: BlockStatus }) {
  if (status === "optional") return <Minus className="size-4 shrink-0 text-muted-foreground/50" aria-hidden="true" />;
  if (status === "green") return <CheckCircle2 className="size-4 shrink-0 text-success-dark" aria-hidden="true" />;
  return <Circle className="size-4 shrink-0 text-muted-foreground/40" aria-hidden="true" />;
}

export function EditLink({ label, onEdit }: { label: string; onEdit: () => void }) {
  return (
    <Button type="button" variant="link" size="sm" className="h-auto shrink-0 p-0" aria-label={`${label} bearbeiten`} onClick={onEdit}>
      Bearbeiten <span aria-hidden="true">→</span>
    </Button>
  );
}

export const ACCOUNT_CONFIG_ROWS: Array<{
  label: string;
  render: (client: Client) => string;
}> = [
  {
    label: "Rechnungssystem",
    render: (c) => {
      const ac = c.data.accountConfig;
      if (ac.rechnungssystem_satisfactory === "Ja") {
        const name = ac.rechnungssystem_name === "Andere" ? `Andere (${ac.rechnungssystem_other || "-"})` : ac.rechnungssystem_name || "-";
        return `${name} | Schnittstelle: ${ac.rechnungssystem_interface_needed || "-"}`;
      }
      return `Nein | Überblick erwünscht: ${ac.rechnungssystem_wants_overview || "-"}`;
    },
  },
  {
    label: "ERP/CSV Datenimport",
    render: (c) => c.data.accountConfig.data_import_external || "-",
  },
  {
    label: "Auftragsnummernkreis",
    render: (c) => c.data.accountConfig.order_number_type || "-",
  },
  {
    label: "Fahrzeugpflicht bei Abschluss",
    render: (c) => {
      const ac = c.data.accountConfig;
      return ac.vehicle_required_on_finish === "Ja" ? `Ja | Feste Zuordnung: ${ac.vehicle_fixed_allocation || "-"}` : ac.vehicle_required_on_finish || "-";
    },
  },
  {
    label: "GPS-Spurzeichnung & OBD-Ortung",
    render: (c) => `Fahrspur: ${c.data.accountConfig.gps_track_recording || "-"} | Geotab: ${c.data.accountConfig.gps_geotab_obd || "-"}`,
  },
  {
    label: "Automatischer Belegversand",
    render: (c) => `CC: ${c.data.accountConfig.receipt_cc_self || "-"} | Durchschlag: ${c.data.accountConfig.receipt_auto_copy || "-"}`,
  },
  {
    label: "Bilder & Anhänge im Portal",
    render: (c) => c.data.accountConfig.upload_docs_images || "-",
  },
  {
    label: "Mobiler Urlaubsplaner & Zeiterfassung",
    render: (c) => `Urlaub: ${c.data.accountConfig.use_vacation_planner || "-"} | Zeiterfassung: ${c.data.accountConfig.use_mobile_time_tracking || "-"}`,
  },
  {
    label: "Dispoboard & Karte",
    render: (c) => `Dispoboard: ${c.data.accountConfig.use_dispoboard || "-"} | Karte: ${c.data.accountConfig.show_resources_on_map || "-"}`,
  },
  {
    label: "Waage & Schranke/Hoftor",
    render: (c) => `Waage: ${c.data.accountConfig.scale_integration_needed || "-"} | Hoftor: ${c.data.accountConfig.gate_control_needed || "-"}`,
  },
];

export function Block({
  id,
  title,
  className,
  count,
  action,
  meta,
  hideEmpty,
  isEmpty,
  children,
}: {
  id?: string;
  title: string;
  className?: string;
  count?: number;
  action?: ReactNode;
  meta?: BlockMetaMap;
  /**
   * "block": Titel, Status und Bearbeiten-Link verschwinden mit (Admin-Sheet
   * – dort gibt es ohnehin keine Bearbeiten-Links, ein leerer Titel wäre nur
   * Ballast). "content": Titel/Status/Bearbeiten-Link bleiben stehen, nur die
   * leere Tabelle darunter wird ausgeblendet (Wizard-Abschluss – der Kunde
   * muss weiterhin sehen, was fehlt, und dorthin springen können).
   */
  hideEmpty?: "block" | "content";
  /** Override für Blöcke ohne aussagekräftigen count (z. B. Freitext, Ja/Nein-Flag). */
  isEmpty?: boolean;
  children: ReactNode;
}) {
  const entry = id ? meta?.[id] : undefined;
  const empty = isEmpty ?? count === 0;
  if (hideEmpty === "block" && empty) return null;
  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between gap-4">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          {entry?.status ? <BlockStatusIcon status={entry.status} /> : null}
          {title}
          {count === undefined ? null : <Badge variant="secondary">{count}</Badge>}
        </h4>
        {action ?? (entry?.onEdit ? <EditLink label={title} onEdit={entry.onEdit} /> : null)}
      </div>
      {hideEmpty === "content" && empty ? null : children}
    </div>
  );
}

export function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-2 py-3">
      <dt className="text-sm font-medium text-foreground">{label}</dt>
      <dd className="text-sm break-words">{value || "-"}</dd>
    </div>
  );
}

export function Th({ children, className }: { children: ReactNode; className?: string }) {
  return <TableHead className={cn("text-sm font-medium text-foreground", className)}>{children}</TableHead>;
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-6 text-center text-muted-foreground">
        {children}
      </TableCell>
    </TableRow>
  );
}

export function ColorSwatch({ value, label = value }: { value: string; label?: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-block size-3.5 rounded-full border border-border" style={{ background: value }} />
      {label}
    </span>
  );
}

/* ------------------------------------------------------------- Abschnitte */

export function Firmendaten({ client, onDownload, meta }: { client: Client; onDownload?: (file: { id: string; name: string }) => void; meta?: BlockMetaMap }) {
  const f = client.data.firmendaten;
  const logo = client.data.documents.uploadedFiles.find((file) => file.type === "Logo");

  return (
    <div className="flex flex-col gap-6">
      <Block id="firmendaten" meta={meta} title="Unternehmen">
        <dl className="grid grid-cols-1 gap-x-6 md:grid-cols-1 divide-y divide-border">
          <Field label="Firma" value={client.name} />
          <div className="md:col-span-1">
            <Field
              label="Adresse"
              value={
                <>
                  {f.street}
                  <br />
                  {f.zip} {f.city}
                </>
              }
            />
          </div>
          <Field
            label="Farbe"
            value={
              <span className="flex flex-col items-start gap-2">
                <ColorSwatch value={f.primaryColor || "#000000"} label={describeBrandColor(f.primaryColor, f.primaryRal, f.primaryCmyk) || undefined} />
                <ColorSwatch value={f.secondaryColor || "#ff9900"} label={describeBrandColor(f.secondaryColor, f.secondaryRal, f.secondaryCmyk) || undefined} />
              </span>
            }
          />
          <div className="md:col-span-1">
            <Field
              label="Hochgeladenes Firmenlogo"
              value={
                logo ? (
                  <span className="inline-flex items-center gap-2">
                    {logo.name} ({logo.size})
                    {onDownload ? (
                      <Button size="xs" variant="outline" onClick={() => onDownload(logo)}>
                        Laden
                      </Button>
                    ) : null}
                  </span>
                ) : null
              }
            />
          </div>
        </dl>
      </Block>
      <Block id="firmendaten" meta={meta} title="Ansprechpartner">
        <dl className="grid grid-cols-1 gap-x-6 md:grid-cols-1 divide-y divide-border">
          <Field label="Name" value={`${f.contactFirstName} ${f.contactLastName}`.trim()} />
          <Field label="Telefon" value={f.phone} />
          <Field label="E-Mail" value={f.email} />
        </dl>
      </Block>
    </div>
  );
}

/** Nur für den Admin-Report: zeigt das Passwort im Klartext mit Kopieren-Button. Im Kunden-Report (Abschluss-Schritt) wird `showPasswords` nicht gesetzt. */
function PasswordCell({ password }: { password: string }) {
  const [copied, setCopied] = useState(false);

  if (!password) return <span className="text-muted-foreground">-</span>;

  return (
    <span className="inline-flex items-center gap-1.5 font-mono">
      {password}
      <Button
        type="button"
        size="icon-xs"
        variant="ghost"
        title="Passwort kopieren"
        onClick={async () => {
          await navigator.clipboard.writeText(password);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <CopyCheck className="size-3.5 text-success-dark" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
      </Button>
    </span>
  );
}

export function TeamsUndBenutzer({ client, meta, showPasswords, hideEmpty }: { client: Client; meta?: BlockMetaMap; showPasswords?: boolean; hideEmpty?: "block" | "content" }) {
  const { teams, users } = client.data.usersTeams;

  return (
    <div className="flex flex-col gap-6">
      <Block id="teams" meta={meta} title="Teams" count={teams.length} hideEmpty={hideEmpty}>
        <Table>
          <TableHeader>
            <TableRow>
              <Th className="sm:pl-0">Team</Th>
              <Th>Mitglieder &amp; Teamrollen</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {teams.length === 0 ? (
              <EmptyRow colSpan={2}>Keine Teams erfasst.</EmptyRow>
            ) : (
              teams.map((team, index) => (
                <TableRow key={team}>
                  <TableCell className="sm:pl-0">
                    {team} (Nr. {index + 1})
                  </TableCell>
                  <TableCell>
                    {users
                      .filter((user) => user.teams.includes(team))
                      .map((user) => `${user.firstname} ${user.lastname} (${user.teamRoleMap?.[team] || user.teamRole || "Mitglied"})`)
                      .join(", ") || "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Block>

      <Block id="benutzer" meta={meta} title="Mitarbeiterkonten" count={users.length} hideEmpty={hideEmpty}>
        <Table>
          <TableHeader>
            <TableRow>
              <Th className="sm:pl-0">Name</Th>
              <Th>User</Th>
              {showPasswords ? <Th>Passwort</Th> : null}
              <Th>Rolle</Th>
              <Th>Zugeordnete Teams</Th>
              <Th>Arbeitszeit / Urlaub</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <EmptyRow colSpan={showPasswords ? 6 : 5}>Keine Benutzer erfasst.</EmptyRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.username}>
                  <TableCell className="sm:pl-0">
                    {user.firstname} {user.lastname}
                  </TableCell>
                  <TableCell>{user.username}</TableCell>
                  {showPasswords ? (
                    <TableCell>
                      <PasswordCell password={user.password} />
                    </TableCell>
                  ) : null}
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.teams.map((team, index) => `${team} (Nr. ${index + 1}, ${user.teamRoleMap?.[team] || "Mitglied"})`).join(", ")}</TableCell>
                  <TableCell>{user.hasWorkingHours ? [`${user.weeklyHours || "-"} Std/Woche`, `${user.workdaysPerWeek || "-"} Tage/Woche`, `${user.vacationDays || "-"} Tage Urlaub`, user.workingTimeFrame ? `Uhrzeit: ${user.workingTimeFrame}` : null].filter(Boolean).join(" | ") : "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Block>
    </div>
  );
}

export function FahrzeugeUndRessourcen({ client, meta, hideEmpty }: { client: Client; meta?: BlockMetaMap; hideEmpty?: "block" | "content" }) {
  const { vehicles } = client.data;
  const { resources } = client.data.ressourcen;

  return (
    <div className="flex flex-col gap-6">
      <Block id="fahrzeuge" meta={meta} title="Fahrzeuge" count={vehicles.length} hideEmpty={hideEmpty}>
        <Table>
          <TableHeader>
            <TableRow>
              <Th className="sm:pl-0">Kennzeichen</Th>
              <Th>Status</Th>
              <Th>Fahrzeuggruppe</Th>
              <Th>Interne ID</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vehicles.length === 0 ? (
              <EmptyRow colSpan={4}>Keine Fahrzeuge erfasst.</EmptyRow>
            ) : (
              vehicles.map((vehicle, index) => (
                <TableRow key={index}>
                  <TableCell className="sm:pl-0">{vehicle.plate}</TableCell>
                  <TableCell>
                    <StatusBadge tone={vehicle.status === "OK" ? "green" : "yellow"}>{vehicle.status}</StatusBadge>
                  </TableCell>
                  <TableCell>{vehicle.group}</TableCell>
                  <TableCell>{vehicle.internalId || "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Block>

      <Block id="ressourcen" meta={meta} title="Ressourcen" count={resources.length} hideEmpty={hideEmpty}>
        <Table>
          <TableHeader>
            <TableRow>
              <Th className="sm:pl-0">Name</Th>
              <Th>Typ</Th>
              <Th>Abrechenbar</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {resources.length === 0 ? (
              <EmptyRow colSpan={3}>Keine Ressourcen erfasst.</EmptyRow>
            ) : (
              resources.map((resource, index) => (
                <TableRow key={index}>
                  <TableCell className="sm:pl-0">{resource.name}</TableCell>
                  <TableCell>
                    {/* ?? statt ||: typeNumber 0 ist eine Ressource ohne Typ, kein fehlendes Feld — siehe csvExport.ts */}
                    {resource.type} (Typ-Nr. {resource.typeNumber ?? index + 1})
                  </TableCell>
                  <TableCell>
                    <StatusBadge tone={resource.billable === "JA" ? "green" : "red"}>{resource.billable}</StatusBadge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Block>
    </div>
  );
}

export function Auftraege({ client, onDownload, meta, hideEmpty }: { client: Client; onDownload?: (file: { id: string; name: string }) => void; meta?: BlockMetaMap; hideEmpty?: "block" | "content" }) {
  const wf = client.data.workflows;

  return (
    <div className="flex flex-col gap-6">
      <Block id="ablauf" meta={meta} title="Ablaufbeschreibung" hideEmpty={hideEmpty} isEmpty={!wf.workflowDescription}>
        <p className="text-sm whitespace-pre-line">{wf.workflowDescription || "Keine Angabe"}</p>
      </Block>

      <Block id="auftragsarten" meta={meta} title="Erfasste Auftragsarten" count={client.data.orderTypes.length} hideEmpty={hideEmpty}>
        <Table>
          <TableHeader>
            <TableRow>
              <Th className="sm:pl-0">Bezeichnung</Th>
              <Th>Kürzel</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {client.data.orderTypes.length === 0 ? (
              <EmptyRow colSpan={2}>Keine Auftragsarten erfasst.</EmptyRow>
            ) : (
              client.data.orderTypes.map((orderType) => (
                <TableRow key={orderType.name}>
                  <TableCell className="sm:pl-0">{orderType.name}</TableCell>
                  <TableCell>
                    <code>{orderType.shortcut}</code>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Block>

      <Block id="auftragszettel" meta={meta} title="Hochgeladene Auftragszettel &amp; Vorlagen" count={wf.files.length} hideEmpty={hideEmpty}>
        <Table>
          <TableHeader>
            <TableRow>
              <Th className="sm:pl-0">Dateiname</Th>
              <Th>Dateigröße</Th>
              <Th>Zugeordneter Auftragstyp</Th>
              {onDownload ? <Th className="text-right">Aktion</Th> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {wf.files.length === 0 ? (
              <EmptyRow colSpan={onDownload ? 4 : 3}>Keine Dateien hochgeladen.</EmptyRow>
            ) : (
              wf.files.map((file, index) => (
                <TableRow key={index}>
                  <TableCell className="sm:pl-0">{file.name}</TableCell>
                  <TableCell>{file.size}</TableCell>
                  <TableCell>
                    <StatusBadge tone="blue">{file.categoryValue || "-"}</StatusBadge>
                  </TableCell>
                  {onDownload ? (
                    <TableCell className="text-right">
                      <Button size="xs" variant="outline" title={`${file.name} herunterladen`} onClick={() => onDownload({ id: "", name: file.name })}>
                        Laden
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Block>
    </div>
  );
}

export function Reports({ client, meta, hideEmpty }: { client: Client; meta?: BlockMetaMap; hideEmpty?: "block" | "content" }) {
  const rep = client.data.reports;

  return (
    <div className="flex flex-col gap-6">
      <Block id="standardreports" meta={meta} title="Standard-Reports" count={rep.standardReports.length} hideEmpty={hideEmpty}>
        {rep.standardReports.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Standardreports gewählt</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {rep.standardReports.map((name) => (
              <StatusBadge key={name} tone="blue">
                {name}
              </StatusBadge>
            ))}
          </div>
        )}
      </Block>

      <Block id="individuellereports" meta={meta} title="Individuelle Reports" hideEmpty={hideEmpty} isEmpty={!rep.hasCustomRequest && !rep.customReportsNotes} action={<StatusBadge tone={rep.hasCustomRequest ? "green" : "red"}>{rep.hasCustomRequest ? "Angefordert" : "Nicht angefordert"}</StatusBadge>}>
        <p className="text-sm">{rep.customReportsNotes || "Keine individuellen Reports angegeben."}</p>
      </Block>
    </div>
  );
}

/**
 * Eigene Komponente statt Teil von Reports: der Abschluss-Schritt zeigt die
 * Dokumente unter den Firmendaten, die Reports aber in einem anderen Abschnitt.
 */
export function Dokumente({ client, onDownload, meta, hideEmpty }: { client: Client; onDownload?: (file: { id: string; name: string }) => void; meta?: BlockMetaMap; hideEmpty?: "block" | "content" }) {
  const files = client.data.documents.uploadedFiles;

  return (
    <Block id="dokumente" meta={meta} title="Alle hochgeladenen Dokumente des Kunden" count={files.length} hideEmpty={hideEmpty}>
      <Table>
        <TableHeader>
          <TableRow>
            <Th className="sm:pl-0">Dateiname</Th>
            <Th>Dateigröße</Th>
            <Th>Dokumententyp / Zweck</Th>
            {onDownload ? <Th className="text-right">Aktion</Th> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.length === 0 ? (
            <EmptyRow colSpan={onDownload ? 4 : 3}>Keine Dokumente hochgeladen.</EmptyRow>
          ) : (
            files.map((file) => (
              <TableRow key={file.id || file.name}>
                <TableCell className="sm:pl-0">{file.name}</TableCell>
                <TableCell>{file.size}</TableCell>
                <TableCell>{file.type || "Dokument"}</TableCell>
                {onDownload ? (
                  <TableCell className="text-right">
                    <Button size="xs" variant="outline" title={`${file.name} herunterladen`} onClick={() => onDownload(file)}>
                      Laden
                    </Button>
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Block>
  );
}

/**
 * Adresstypen fehlten bisher in beiden Berichten, obwohl der Wizard sie als
 * eigenes Stammdaten-Submodul erfasst.
 */
export function Adresstypen({ client, meta, hideEmpty }: { client: Client; meta?: BlockMetaMap; hideEmpty?: "block" | "content" }) {
  const { types, addresses, noAddresses } = client.data.adresstypen;

  if (noAddresses) {
    return <p className="text-sm text-muted-foreground">Der Kunde hat angegeben, dass keine festen Adressen benötigt werden.</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <Block id="adresstypen" meta={meta} title="Adresstypen" count={types.length} hideEmpty={hideEmpty}>
        {types.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Adresstypen erfasst.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {types.map((type) => (
              <StatusBadge key={type} tone="blue">
                {type}
              </StatusBadge>
            ))}
          </div>
        )}
      </Block>

      <Block id="adressen" meta={meta} title="Adressen" count={addresses.length} hideEmpty={hideEmpty}>
        <Table>
          <TableHeader>
            <TableRow>
              <Th className="sm:pl-0">Name</Th>
              <Th>Typ</Th>
              <Th>Straße</Th>
              <Th>Ort</Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {addresses.length === 0 ? (
              <EmptyRow colSpan={4}>Keine Adressen erfasst.</EmptyRow>
            ) : (
              addresses.map((address, index) => (
                <TableRow key={index}>
                  <TableCell className="sm:pl-0">{address.name}</TableCell>
                  <TableCell>{address.type || "-"}</TableCell>
                  <TableCell>{address.street || "-"}</TableCell>
                  {/*
                    PLZ und Ort zusammen: der CSV-Import trennt `PLZ_Ort` seit
                    S8.2, und `city` allein zeigte dem Kunden seine eigene
                    Adresse ohne Postleitzahl.
                  */}
                  <TableCell>{[address.zip, address.city].filter(Boolean).join(" ") || "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Block>
    </div>
  );
}
